import React from "react";

import {
  effectiveRiskLevel,
  WebTracker,
  webTrackerRiskLevelAsInt,
} from "../types";
import { WebTrackerOverviewColumnName } from "../useWebTrackerOverviewColumns";

export type WebTrackerManagerSortDirection = "asc" | "desc";

export type WebTrackerManagerSort = {
  column: WebTrackerOverviewColumnName;
  direction: WebTrackerManagerSortDirection;
};

export type WebTrackerManagerColumn = {
  name: WebTrackerOverviewColumnName;
  tooltip: string;
  isSortable: boolean;
  align?: "center" | "inherit" | "justify" | "left" | "right";
  tableCellStyles?: React.CSSProperties;
  labelStyles?: React.CSSProperties;
};

export type WebTrackerManagerRow = {
  onNumPages: number;
  firstDetectedAt: Date;
  lastDetectedAt: Date | null;
  webTracker: WebTracker;
};

export const sortByNameFn = (sort: WebTrackerManagerSort) => {
  return (a: WebTrackerManagerRow, b: WebTrackerManagerRow) => {
    const aName = a.webTracker.name || "";
    const bName = b.webTracker.name || "";

    return sort.direction === "asc"
      ? aName.localeCompare(bName)
      : bName.localeCompare(aName);
  };
};

export const sortByPagesDetectedFn = (sort: WebTrackerManagerSort) => {
  return (a: WebTrackerManagerRow, b: WebTrackerManagerRow) => {
    const aPages = a.onNumPages || 0;
    const bPages = b.onNumPages || 0;

    const byPages =
      sort.direction === "asc" ? aPages - bPages : bPages - aPages;

    if (byPages !== 0) {
      return byPages;
    }

    return sortByNameFn({ ...sort, direction: "asc" })(a, b);
  };
};

export const sortByRiskFn = (sort: WebTrackerManagerSort) => {
  return (a: WebTrackerManagerRow, b: WebTrackerManagerRow) => {
    const aRisk = webTrackerRiskLevelAsInt(effectiveRiskLevel(a.webTracker));
    const bRisk = webTrackerRiskLevelAsInt(effectiveRiskLevel(b.webTracker));

    const byRisk = sort.direction === "asc" ? aRisk - bRisk : bRisk - aRisk;

    if (byRisk !== 0) {
      return byRisk;
    }

    return sortByPagesDetectedFn({ ...sort, direction: "desc" })(a, b);
  };
};

export const sortByFirstDetectedFn = (sort: WebTrackerManagerSort) => {
  return (a: WebTrackerManagerRow, b: WebTrackerManagerRow) => {
    const aFirstDetected = a.firstDetectedAt.getTime();
    const bFirstDetected = b.firstDetectedAt.getTime();

    const byFirstDetected =
      sort.direction === "asc"
        ? aFirstDetected - bFirstDetected
        : bFirstDetected - aFirstDetected;

    if (byFirstDetected !== 0) {
      return byFirstDetected;
    }

    return sortByRiskFn({ ...sort, direction: "asc" })(a, b);
  };
};

export const sortByLastDetectedFn = (sort: WebTrackerManagerSort) => {
  return (a: WebTrackerManagerRow, b: WebTrackerManagerRow) => {
    const distantFuture = new Date(8640000000000000);

    const aLastDetected = a.lastDetectedAt
      ? a.lastDetectedAt.getTime()
      : distantFuture.getTime();
    const bLastDetected = b.lastDetectedAt
      ? b.lastDetectedAt.getTime()
      : distantFuture.getTime();

    const byLastDetected =
      sort.direction === "asc"
        ? aLastDetected - bLastDetected
        : bLastDetected - aLastDetected;

    if (byLastDetected !== 0) {
      return byLastDetected;
    }

    return sortByRiskFn({ ...sort, direction: "asc" })(a, b);
  };
};
